/**
 * Festive cart offers.
 *
 * Offers A and B are CLAIMED by hand: two may be unlocked at once but only ONE
 * may be claimed. Offer C — the combo price — has no Claim button at all;
 * qualifying is the claim. enforceCombo() applies its code the moment the cart
 * earns it and takes it back the moment it stops, which is why the enforcement
 * pass below runs in both directions rather than only taking things away.
 *
 * All of this was verified against the live store:
 *   - GET /discount/<code> APPENDS; codes pile up in cart.discount_codes.
 *     It also 302s off-origin, which 401s behind `shopify theme dev`.
 *   - POST /cart/update.js {discount: '<code>'} REPLACES the whole set,
 *     works on localhost, and returns the updated cart. That is what we use,
 *     so one-offer-at-a-time is enforced by Shopify with no clearing step.
 *
 * Every claim is: adjust lines -> apply the code -> ASSERT it came back
 * applicable. A code can exist and still be inapplicable (Buy X Get Y with
 * no Y in the cart), which would otherwise leave a full-price line wearing
 * an "Offer Applied" badge.
 *
 *   claim gift     -> add gift variant, apply code, assert
 *   claim discount -> remove gift line, apply code, assert
 *
 * Claiming is only half the job. The gift line has to be POLICED for as long as
 * it sits in the cart, because nothing else will:
 *
 *   - Shopify will not revoke it. Whatever minimum the code carries is measured
 *     over the whole cart, gift included (1499 shirt + 899 gel = 2398, which
 *     clears 1499), while the threshold shoppers are shown deliberately excludes
 *     the gift. So the code stays applicable long after the qualifying spend has
 *     gone. Drop the cart under the threshold and the drawer would beg for
 *     "₹600 more" directly above a free shirt it had already given away.
 *   - The gift product is an ordinary storefront product. Anyone who reaches it
 *     from search or a collection can add it again, and every copy comes out
 *     free while the code is live.
 *
 * enforceGiftLines() closes both: after any cart write, and once on load so
 * carts already in a bad state heal themselves, it holds the cart to a single
 * rule — the gift is present only while the qualifying spend earns it, and then
 * only once.
 *
 * Enforcement is asynchronous, though, and that gap was itself exploitable: a
 * shopper who removed a product and clicked Checkout inside it reached payment
 * with a free shirt the cart no longer earned. Removing the latency is not
 * possible — the correction is several round-trips by nature — so instead the
 * checkout waits for it. verifyForCheckout() below is installed into the gate
 * in snippets/cart-offers-gate.liquid, which holds any checkout attempt until
 * the cart has been read fresh and confirmed. See that snippet for why the
 * listener has to be registered from the <head>.
 */
(function () {
	'use strict';

	var SELECTORS = [
		'.side-cart .cart-items',
		'.side-cart .cart-offers-milestone',
		'.side-cart .cart-offers-panel',
		// The combo message renders INSIDE the free-shipping band (see
		// snippets/cart-progress-bar.liquid), so it is this wrapper that has to
		// be repainted when the combo turns on or off — not a banner of its own.
		'.side-cart .cart-progress-bar-wrapper',
		'.side-cart .cart-combo-nudge',
		// Free Under Eye Roll-On: the partly-met progress block and the add-back
		// card both change when the gift is granted or taken back.
		'.side-cart .cart-freebie-progress',
		'.side-cart .cart-freebie-cta-wrap',
		'.side-cart .sub-total'
	];

	/**
	 * Cross-script render sequencing.
	 *
	 * Shared with sections/rakhi-kit-grid.liquid, which repaints this same
	 * drawer through its own independent fetch. Two GET requests for
	 * `?sections=side-cart,...` — one from here, one from that page's own
	 * add-to-cart flow — can resolve out of order. Without this, whichever
	 * response happened to land LAST would win even when it was issued
	 * FIRST, silently reverting a drawer (footer, and its Checkout button,
	 * included) that a later, more current response had already painted.
	 * Tokens are handed out at ISSUE time and compared at APPLY time so the
	 * most recently issued render always wins, whichever fetch finishes first.
	 */
	window.__cartDrawerRender = window.__cartDrawerRender || { seq: 0, applied: 0 };

	function nextRenderToken() {
		return ++window.__cartDrawerRender.seq;
	}

	function shouldApplyRender(token) {
		if (token < window.__cartDrawerRender.applied) return false;
		window.__cartDrawerRender.applied = token;
		return true;
	}

	var busy = false;

	// Depth counter, not a boolean: claims nest cart writes inside cart writes,
	// and a boolean would be cleared by the inner one finishing first.
	var suppressed = 0;

	/* ------------------------------------------------------------------ *
	 * Checkout gate handshake
	 * ------------------------------------------------------------------ */

	/**
	 * The gate is rendered only while offer A is live, so treat it as optional
	 * and no-op when it is absent — nothing here should break once the promo is
	 * switched off in the theme editor.
	 */
	function gate() {
		return window.CartOffersGate || null;
	}

	function markDirty() {
		var g = gate();
		if (g) g.markDirty();
	}

	/**
	 * Cart writes currently in flight, ANY origin — the shopper's own quantity
	 * change as much as our enforcement writes.
	 *
	 * This is what closes the nastiest version of the race. Verifying on a
	 * checkout click is no use if a change.js is still on the wire: /cart.js
	 * would hand back the PRE-write cart, which still clears the threshold, the
	 * gate would call it clean, and the shopper would be forwarded — with the
	 * write landing a moment later and taking the cart below the threshold
	 * while they are already in checkout. So verification waits for silence
	 * first.
	 */
	var pendingWrites = 0;
	var settleWaiters = [];

	// A hung request must not trap the shopper behind the gate forever.
	var SETTLE_TIMEOUT = 8000;

	function releaseSettleWaiters() {
		var waiting = settleWaiters.splice(0);
		waiting.forEach(function (resolve) { resolve(); });
	}

	function noteWriteStart() {
		pendingWrites++;
	}

	function noteWriteEnd() {
		pendingWrites = Math.max(0, pendingWrites - 1);
		if (pendingWrites === 0) releaseSettleWaiters();
	}

	function whenSettled() {
		if (pendingWrites === 0) return Promise.resolve();
		return new Promise(function (resolve) {
			settleWaiters.push(resolve);
			setTimeout(resolve, SETTLE_TIMEOUT);
		});
	}

	/**
	 * Counts only SHOPPER-driven cart writes, so a verification can tell whether
	 * the cart moved under it while it was working. Our own corrective writes
	 * run inside unpoliced() and must not invalidate the very verification that
	 * issued them.
	 */
	var shopperWrites = 0;

	/**
	 * Confirm the cart against every live offer and report back to the gate.
	 *
	 * Resolves { changed, failed, message }:
	 *   changed — the shopper LOST something, so they must NOT be forwarded to
	 *             checkout; they are looking at a cart that has just lost a line
	 *             or a discount. A cart that merely got cheaper does not count:
	 *             offer C applies its code here, and holding a shopper under a
	 *             warning to tell them they saved money would be absurd.
	 *   message — why, in the shopper's words, for the gate to display.
	 *   failed  — we could not establish the cart's state at all. Fails OPEN:
	 *             a network blip must not block a legitimate order. Blocking
	 *             every unverifiable checkout would cost real revenue to guard
	 *             against a case this rare.
	 */
	function verifyForCheckout() {
		var revocationsAtClick = revocations;

		return whenSettled()
			.then(function () {
				var seen = shopperWrites;
				// No `known` cart on purpose: a checkout attempt gets an
				// authoritative read, never a cached body from an earlier write.
				return serialised(function () { return enforceAll(); }).then(function (result) {
					// A pass that ran between the click and here counts too — the
					// shopper still needs telling that something is gone, whichever
					// pass actually took it.
					//
					// Only REVOCATIONS hold the checkout. The combo pass can also
					// change the cart by applying a code, which makes it cheaper —
					// forwarding the shopper with a better price is correct, and
					// stopping them under a warning would not be.
					var corrected = result.revoked || revocations !== revocationsAtClick;

					function settle() {
						// Only vouch for the cart if the shopper did not change it
						// again while we were checking.
						if (shopperWrites === seen) {
							var g = gate();
							if (g) g.markClean();
						}
						return {
							changed: corrected,
							failed: false,
							message: result.message || lastRevokedMessage
						};
					}

					// Only our own pass owes a repaint; an earlier pass already
					// painted whatever it changed.
					if (!result.changed) return settle();

					// /cart renders no drawer, so there is nothing to repaint and
					// the page would keep showing the line we just took away.
					if (!document.querySelector('.side-cart')) {
						window.location.reload();
						return {
							changed: corrected,
							failed: false,
							message: result.message || lastRevokedMessage
						};
					}
					return refresh().then(settle);
				});
			})
			.catch(function (err) {
				console.error('[cart-offers] checkout verification failed', err);
				return { changed: false, failed: true };
			});
	}

	function postJSON(url, body) {
		return fetch(url, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			body: JSON.stringify(body)
		}).then(function (r) {
			return r.json().then(function (data) {
				if (!r.ok) throw new Error(data.description || data.message || 'Cart error');
				return data;
			});
		});
	}

	/**
	 * Apply a discount code and return the updated cart.
	 *
	 * Uses the Ajax cart API rather than GET /discount/<code>. The redirect
	 * endpoint 302s off to the storefront, which returns 401 through the
	 * `shopify theme dev` proxy, so claims silently failed on localhost.
	 * This call is same-origin, needs no redirect, hands back the cart to
	 * check, and REPLACES any existing code — so one-offer-at-a-time is
	 * enforced by Shopify itself, with no separate clearing step.
	 */
	/**
	 * Ask a cart write to return the re-rendered drawer alongside the cart.
	 *
	 * The Ajax cart API renders sections inline for add/change/update — theme.js
	 * already relies on this. Piggybacking on the write removes a whole
	 * round-trip from every interaction, and it is the one worth removing: a
	 * standalone ?sections= call re-renders the entire side-cart server-side
	 * (recommendations, complementary products, swiper), so it dominates the
	 * time the shopper actually waits.
	 */
	function withSections(body) {
		body.sections = 'side-cart,cart-count';
		body.sections_url = window.location.pathname;
		return body;
	}

	function applyDiscount(code) {
		if (!code) return Promise.resolve(null);
		return postJSON('/cart/update.js', withSections({ discount: code }));
	}

	/**
	 * Never trust that applying worked. A code can exist yet be inapplicable
	 * (Buy X Get Y with no Y in the cart), in which case Shopify records it
	 * with applicable:false and still charges full price. Treating that as
	 * success is how a full-price line ends up labelled "Offer Applied".
	 */
	function assertApplied(cart, code) {
		if (!code) return cart;
		var codes = (cart && cart.discount_codes) || [];
		var found = codes.filter(function (c) {
			return String(c.code).toUpperCase() === String(code).toUpperCase();
		})[0];
		if (!found) throw new Error('Could not apply ' + code + '.');
		if (!found.applicable) throw new Error(code + ' does not apply to this cart.');
		return cart;
	}

	function getCart() {
		return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
	}


	/**
	 * Re-bind the theme's cart handlers after we replace cart markup.
	 * Same reason as in theme.js btyUpdateHtml: innerHTML replacement discards
	 * the +/- and quantity-input handlers, leaving dead steppers until reload.
	 * Both bind by assignment, so calling them again is safe.
	 */
	function rebindCartHandlers() {
		try {
			if (typeof btyQuantityButton === 'function') btyQuantityButton();
			if (typeof btyUpdateProductQuantity === 'function') btyUpdateProductQuantity();
			if (typeof btyAddToCart === 'function') btyAddToCart();
		} catch (e) {
			console.error('[cart-offers] rebind failed', e);
		}
	}

	/**
	 * Bring the footer into line with the incoming markup.
	 *
	 * The Subtotal/checkout footer is inside `if cart != empty`, so it is present
	 * or absent rather than merely different, and it sits outside
	 * .side-cart-content — a content swap alone would leave a checkout button
	 * under an empty cart, or none once the cart refilled.
	 */
	function syncFooter(doc) {
		var next = doc.querySelector('.side-cart-footer');
		var curr = document.querySelector('.side-cart-footer');

		if (next && curr) {
			curr.innerHTML = next.innerHTML;
			return;
		}
		if (!next && curr) {
			curr.parentNode.removeChild(curr);
			return;
		}
		if (next && !curr) {
			var inner = document.querySelector('.side-cart-inner');
			// Imported: `next` belongs to the DOMParser document, not this one.
			if (inner) inner.appendChild(document.importNode(next, true));
		}
	}

	/**
	 * Paint already-rendered section HTML into the drawer.
	 *
	 * Split out of refresh() so a cart write that returned its own sections can
	 * reuse this without paying for a second render.
	 */
	function renderSections(data) {
		if (!data) return;

		if (data['side-cart']) {
			var doc = new DOMParser().parseFromString(data['side-cart'], 'text/html');

			/**
			 * The drawer changes SHAPE when the cart empties: side-cart.liquid
			 * wraps its whole body in `if cart == empty`, so an empty cart has no
			 * .cart-items, no offer regions and no footer — just .cart-empty.
			 *
			 * Patching region by region then quietly does nothing, because every
			 * `next` lookup misses and the guard skips the copy, leaving the
			 * previous lines on screen. Revoking the gift is exactly this case
			 * when the gift was the last thing left: the cart really was emptied,
			 * while the shopper went on seeing a free T-shirt they no longer had.
			 * A whole-inner swap is the only thing that can cross that boundary.
			 */
			var nextItems = doc.querySelector('.side-cart .cart-items');
			var currItems = document.querySelector('.side-cart .cart-items');
			var shapeChanged = !nextItems !== !currItems;

			if (shapeChanged) {
				// Swap the drawer's BODY, never the whole inner. The header holds
				// the close button, and btyClosePopup binds its handler to that
				// exact node when the drawer opens — nothing re-binds it. Replacing
				// the header therefore leaves a drawer that cannot be closed.
				// This mirrors how theme.js itself crosses this transition in
				// btyFetchCart: swap .side-cart-content, then drop the footer.
				var nextContent = doc.querySelector('.side-cart-content');
				var currContent = document.querySelector('.side-cart-content');
				if (nextContent && currContent) currContent.innerHTML = nextContent.innerHTML;
				syncFooter(doc);
			} else {
				SELECTORS.forEach(function (sel) {
					var next = doc.querySelector(sel);
					var curr = document.querySelector(sel);
					if (next && curr) curr.innerHTML = next.innerHTML;
				});
			}
		}
		if (data['cart-count']) {
			if (typeof btyCartItemCount === 'function' && typeof btyGetSectionHtml === 'function') {
				btyCartItemCount(btyGetSectionHtml(data['cart-count'], '.shopify-section'));
			}
		}
		rebindCartHandlers();
		syncFreebieNudge();
	}

	/** Re-render the offer-bearing regions from the server. */
	function refresh() {
		var token = nextRenderToken();
		var path = window.location.pathname;
		return fetch(path + '?sections=side-cart,cart-count', { credentials: 'same-origin' })
			.then(function (r) { return r.json(); })
			.then(function (data) {
				if (shouldApplyRender(token)) renderSections(data);
			});
	}

	/**
	 * Surface the failure in the drawer. Shopify's most common rejection here
	 * is a 422 "already sold out" when the gift product has no inventory —
	 * silent console errors make that look like a broken button.
	 */
	function showError(btn, err, fallback) {
		console.error('[cart-offers]', err);

		// The combo nudge ships its own message node; the offer cards do not and
		// get one appended. Looking for both means a failed nudge does not
		// silently grow a second, unstyled paragraph beside the one already
		// sitting in the markup.
		var host = btn.closest('.cart-combo-nudge__info') || btn.closest('.cart-offer-card') || btn.parentNode;
		if (!host) return;

		var msg = host.querySelector('.cart-combo-nudge__error, .cart-offer-card__error');
		if (!msg) {
			msg = document.createElement('p');
			msg.className = 'cart-offer-card__error';
			host.appendChild(msg);
		}
		msg.textContent = (err && err.message) || fallback || 'Could not claim this offer.';
	}

	function setBusy(btn, on) {
		busy = on;
		if (!btn) return;
		btn.classList.toggle('is-loading', on);
		btn.disabled = on;
	}

	/** Remove every line belonging to the gift product. */
	function removeGiftLines(giftHandle) {
		if (!giftHandle) return Promise.resolve();
		return getCart().then(function (cart) {
			var keys = cart.items
				.filter(function (i) { return i.handle === giftHandle; })
				.map(function (i) { return i.key; });
			return keys.reduce(function (chain, key) {
				return chain.then(function () {
					return postJSON('/cart/change.js', { id: key, quantity: 0 });
				});
			}, Promise.resolve());
		});
	}

	/* ------------------------------------------------------------------ *
	 * Gift-line enforcement
	 * ------------------------------------------------------------------ */

	/**
	 * Offer A's terms, printed into the always-rendered panel wrapper by
	 * cart-offers.liquid. Read from the wrapper's ATTRIBUTES rather than its
	 * contents on purpose: the offer cards inside disappear the moment the gift
	 * is claimed, so anything stored in them is gone exactly when enforcement
	 * needs it. Attributes also survive the innerHTML swaps in refresh() and
	 * btyUpdateHtml. Absent when offers are off, which disables enforcement.
	 */
	function offerTerms() {
		var el = document.querySelector('[data-offers-config]');
		if (!el) return null;

		var handle = el.getAttribute('data-offer-a-handle');
		var threshold = parseInt(el.getAttribute('data-offer-a-threshold'), 10);
		if (!handle || !threshold) return null;

		return {
			handle: handle,
			threshold: threshold,
			code: el.getAttribute('data-offer-a-code') || ''
		};
	}

	/**
	 * Offer C's terms, published by the same config element as offer A's.
	 *
	 * Returns null unless the offer can actually do something: a code plus at
	 * least one way to qualify. Either half of the pair may be absent while the
	 * campaign runs on the kit alone, and vice versa.
	 */
	function comboTerms() {
		var el = document.querySelector('[data-offers-config]');
		if (!el) return null;

		var code = el.getAttribute('data-offer-c-code');
		if (!code) return null;

		var h1 = el.getAttribute('data-offer-c-handle-1');
		var h2 = el.getAttribute('data-offer-c-handle-2');
		var kit = el.getAttribute('data-offer-c-kit-handle');
		if (!(h1 && h2) && !kit) return null;

		return {
			code: code,
			handle1: h1 || '',
			handle2: h2 || '',
			kitHandle: kit || '',
			// A kit already listed at the combo price qualifies for the banner
			// but must NOT take a code, or it drops below the advertised total.
			kitNeedsCode: el.getAttribute('data-offer-c-kit-needs-code') === '1'
		};
	}

	/**
	 * Does this cart qualify, and does qualifying require a code?
	 *
	 * Mirrors the Liquid in snippets/cart-combo.liquid. The two must agree, or
	 * the banner and the discount disagree about the same cart.
	 *
	 * Counts QUANTITIES for the same reason the Liquid does: the combo is priced
	 * per pair, and quantities are summed across lines because one product
	 * reaches the cart as several lines when it has variants. One complete pair
	 * is enough to earn the code — the nudge goes on asking for the rest.
	 */
	function comboState(terms, items) {
		var qty1 = 0;
		var qty2 = 0;
		var hasKit = false;

		(items || []).forEach(function (i) {
			var q = typeof i.quantity === 'number' ? i.quantity : 0;
			if (terms.handle1 && i.handle === terms.handle1) qty1 += q;
			if (terms.handle2 && i.handle === terms.handle2) qty2 += q;
			if (terms.kitHandle && i.handle === terms.kitHandle) hasKit = true;
		});

		var pairs = terms.handle1 && terms.handle2 ? Math.min(qty1, qty2) : 0;
		var active = pairs > 0 || hasKit;

		// A kit priced at the combo already needs no code — but only when the
		// pair is not ALSO complete, which would earn one on its own.
		var needsCode = pairs > 0 || (hasKit && terms.kitNeedsCode);

		return { qty1: qty1, qty2: qty2, pairs: pairs, hasKit: hasKit, active: active, needsCode: needsCode };
	}

	function hasCode(cart, code) {
		var codes = (cart && cart.discount_codes) || [];
		var mine = String(code).toUpperCase();
		return codes.some(function (c) {
			return String(c.code).toUpperCase() === mine;
		});
	}

	/**
	 * Hold the cart to offer C's terms. Resolves true when it had to intervene.
	 *
	 * Unlike offers A and B there is no Claim button — qualifying IS the claim.
	 * So this runs on every cart write in both directions: it applies the code
	 * the moment the cart earns it, and takes it back the moment it stops, which
	 * is what stops a shopper reaching checkout with a combo price for a cart
	 * that no longer holds the combo.
	 */
	function enforceCombo(known) {
		var terms = comboTerms();
		if (!terms) return Promise.resolve(false);

		var load = known ? Promise.resolve(known) : getCart();
		return load.then(function (cart) {
			var state = comboState(terms, (cart && cart.items) || []);
			var on = hasCode(cart, terms.code);

			// Earned, and not yet applied.
			if (state.needsCode && !on) {
				return unpoliced(function () {
					return applyDiscount(terms.code).then(function (updated) {
						return assertApplied(updated, terms.code);
					});
				}).then(function () {
					corrections++;
					// Benign: the cart got CHEAPER. It still needs a repaint, but
					// it must not be reported to the gate as a revocation — that
					// would greet a shopper with a warning for good news.
					return { changed: true, revoked: false };
				}).catch(function (err) {
					// Deliberately swallowed. An inapplicable or expired code is a
					// merchandising problem, not a reason to wedge the cart — and
					// rejecting here would abandon the caller's re-render. The
					// shopper simply sees full price, which is the safe direction
					// to fail in.
					console.error('[cart-offers] could not apply ' + terms.code, err);
					return { changed: false, revoked: false };
				});
			}

			// No longer earned, but still applied. This is the direction that
			// actually protects revenue: without it, removing one half of the
			// pair leaves the combo price standing on a single product.
			if (!state.needsCode && on) {
				return unpoliced(function () {
					return releaseOfferCode(terms.code);
				}).then(function () {
					corrections++;
					revocations++;
					// The cart got MORE expensive. The shopper has to be told
					// before they are forwarded to checkout.
					return { changed: true, revoked: true };
				}).catch(function (err) {
					console.error('[cart-offers] could not release ' + terms.code, err);
					return { changed: false, revoked: false };
				});
			}

			return { changed: false, revoked: false };
		});
	}

	/**
	 * One pass over every live offer.
	 *
	 * Gift enforcement runs first because it can remove lines; the combo pass
	 * then has to look at what is actually left, so a body that gift
	 * enforcement has already invalidated is dropped in favour of a fresh read.
	 *
	 * Resolves { changed, revoked, message }:
	 *   changed — something was written, so the view needs repainting
	 *   revoked — the shopper LOST something, so a checkout must not proceed
	 *             silently. Applying the combo code changes the cart without
	 *             revoking anything, and must not be reported as a loss.
	 */
	function enforceAll(known) {
		return enforceGiftLines(known).then(function (giftChanged) {
			return enforceFreebie(giftChanged ? null : known).then(function (freebie) {
				// The known body describes a cart that no longer exists once gift
				// enforcement has removed a line from it.
				var stale = giftChanged || freebie.changed;
				var comboKnown = !giftChanged && freebie.cart ? freebie.cart : stale ? null : known;
				return enforceCombo(comboKnown).then(function (combo) {
					var revoked = giftChanged || freebie.revoked || combo.revoked;
					var message = null;
					if (giftChanged) {
						message = giftRevokedMessage();
					} else if (freebie.revoked) {
						message = freebieRevokedMessage();
					} else if (combo.revoked) {
						message = comboRevokedMessage();
					}
					// Every revocation reaches the gate through here, so remembering
					// it here covers passes the gate did not itself run.
					if (revoked) lastRevokedMessage = message;
					return {
						changed: giftChanged || freebie.changed || combo.changed,
						revoked: revoked,
						message: message,
						sections: !giftChanged && !combo.changed ? freebie.sections || null : null
					};
				});
			});
		});
	}

	function freebieTerms() {
		var el = document.querySelector('[data-offers-config]');
		if (!el) return null;

		var gift = el.getAttribute('data-offer-d-gift-handle');
		var variant = Number(el.getAttribute('data-offer-d-gift-variant'));
		if (!gift || !variant) return null;

		var rules = [1, 2].map(function (n) {
			var handles = el.getAttribute('data-offer-d-rule-' + n + '-handles');
			var code = el.getAttribute('data-offer-d-rule-' + n + '-code');
			if (!handles || !code) return null;
			return {
				code: code,
				handles: handles.split(','),
				min: parseInt(el.getAttribute('data-offer-d-rule-' + n + '-min'), 10) || 1
			};
		}).filter(Boolean);

		if (!rules.length) return null;
		return { gift: gift, variant: variant, rules: rules };
	}

	function isFreebieLine(terms, item) {
		return !!item && item.handle === terms.gift && !!item.properties && item.properties._freebie === '1';
	}

	function freebieIsFree(terms, items, giftLine) {
		var discounted = 0;
		(items || []).forEach(function (i) {
			if (i.handle === terms.gift) discounted += (i.original_line_price || 0) - (i.final_line_price || 0);
		});
		return !!giftLine && discounted >= (giftLine.original_price || 0);
	}

	function removeFreebieLines(terms) {
		return getCart().then(function (cart) {
			var lines = ((cart && cart.items) || []).filter(function (i) { return isFreebieLine(terms, i); });
			return inSequence(lines, function (line) {
				return postJSON('/cart/change.js', { id: line.key, quantity: 0 });
			});
		});
	}

	function hideFreebieRows() {
		var rows = Array.prototype.slice.call(document.querySelectorAll('.side-cart .product-item--freebie'));
		rows.forEach(function (row) { row.style.display = 'none'; });
		return rows;
	}

	function showFreebiePlaceholder() {
		var host = document.querySelector('.side-cart .cart-items');
		var el = document.querySelector('[data-offers-config]');
		if (!host || !el || host.querySelector('.product-item--freebie')) return;

		var title = el.getAttribute('data-offer-d-gift-title') || '';
		var image = el.getAttribute('data-offer-d-gift-image');
		var price = el.getAttribute('data-offer-d-gift-price') || '';

		var row = document.createElement('div');
		row.className = 'product-item product-item--freebie updating';

		var media = document.createElement('span');
		media.className = 'product-image';
		if (image) {
			var img = document.createElement('img');
			img.src = image;
			img.width = 110;
			img.height = 110;
			img.alt = title;
			media.appendChild(img);
		}

		var info = document.createElement('div');
		info.className = 'product-info';
		var name = document.createElement('h2');
		name.className = 'product-name';
		var text = document.createElement('span');
		text.className = 'product-name-text';
		text.textContent = title;
		name.appendChild(text);

		var priceLine = document.createElement('div');
		priceLine.className = 'price-line';
		var was = document.createElement('s');
		was.className = 'regular-price';
		was.textContent = price;
		var tag = document.createElement('span');
		tag.className = 'cart-freebie-tag';
		tag.textContent = 'FREE';
		priceLine.appendChild(was);
		priceLine.appendChild(tag);

		info.appendChild(name);
		info.appendChild(priceLine);
		row.appendChild(media);
		row.appendChild(info);
		host.appendChild(row);
	}

	function addFreebie(terms) {
		return postJSON('/cart/add.js', { items: [{ id: terms.variant, quantity: 1, properties: { _freebie: '1' } }] });
	}

	function earnedFreebieRule(terms, items) {
		for (var i = 0; i < terms.rules.length; i++) {
			var rule = terms.rules[i];
			var qty = 0;
			(items || []).forEach(function (item) {
				if (!isFreebieLine(terms, item) && rule.handles.indexOf(item.handle) !== -1) {
					qty += typeof item.quantity === 'number' ? item.quantity : 0;
				}
			});
			if (qty >= rule.min) return rule;
		}
		return null;
	}

	function freebieRevokedMessage() {
		return configMessage('data-offer-d-revoked-message');
	}

	var FREEBIE_KEY = 'unbound_freebie_state';

	function freebieState() {
		try { return window.localStorage.getItem(FREEBIE_KEY); } catch (e) { return null; }
	}

	function setFreebieState(value) {
		try {
			if (value) window.localStorage.setItem(FREEBIE_KEY, value);
			else window.localStorage.removeItem(FREEBIE_KEY);
		} catch (e) {}
	}

	function syncFreebieNudge() {
		var declined = freebieState() === 'declined';
		var nodes = document.querySelectorAll('[data-freebie-nudge]');
		for (var i = 0; i < nodes.length; i++) {
			nodes[i].style.display = declined ? '' : 'none';
		}
	}

	function releaseFreebieCodesInBackground(rules) {
		if (!rules.length) return;
		unpoliced(function () {
			return inSequence(rules, function (r) {
				return releaseOfferCode(r.code);
			});
		}).catch(function (err) {
			console.error('[cart-offers] could not release the free gift code', err);
		});
	}

	function finishFreebie(terms, rule) {
		return applyDiscount(rule.code).then(function (updated) {
			assertApplied(updated, rule.code);
			var giftLine = ((updated && updated.items) || []).filter(function (i) { return isFreebieLine(terms, i); })[0];
			if (!freebieIsFree(terms, updated && updated.items, giftLine)) {
				throw new Error(rule.code + ' did not make the free gift ₹0.');
			}
			return updated;
		}).catch(function (err) {
			return removeFreebieLines(terms)
				.then(function () { return releaseOfferCode(rule.code); })
				.then(function () { throw err; });
		});
	}

	function enforceFreebie(known) {
		var none = { changed: false, revoked: false };
		var terms = freebieTerms();
		if (!terms) return Promise.resolve(none);

		var load = known ? Promise.resolve(known) : getCart();
		return load.then(function (cart) {
			var items = (cart && cart.items) || [];
			var gifts = items.filter(function (i) { return isFreebieLine(terms, i); });
			var rule = earnedFreebieRule(terms, items);
			var applied = terms.rules.filter(function (r) { return hasCode(cart, r.code); });

			if (!rule) {
				setFreebieState(null);
				syncFreebieNudge();
				if (!gifts.length && !applied.length) return none;

				var shown = hideFreebieRows();
				return unpoliced(function () {
					var last = null;
					return inSequence(gifts, function (line) {
						var body = { id: line.key, quantity: 0 };
						if (line === gifts[gifts.length - 1]) withSections(body);
						return postJSON('/cart/change.js', body).then(function (updated) {
							last = updated;
						});
					}).then(function () {
						releaseFreebieCodesInBackground(applied);
						return last;
					});
				}).then(function (updated) {
					corrections++;
					if (gifts.length) revocations++;
					return {
						changed: true,
						revoked: gifts.length > 0,
						cart: isCompleteCart(updated) ? updated : null,
						sections: updated && updated.sections ? updated.sections : null
					};
				}, function (err) {
					shown.forEach(function (row) { row.style.display = ''; });
					throw err;
				});
			}

			if (!gifts.length) {
				var state = freebieState();
				if (state === 'on') {
					setFreebieState('declined');
					state = 'declined';
				}
				if (state === 'declined') {
					syncFreebieNudge();
					releaseFreebieCodesInBackground(applied);
					return none;
				}
			}

			var keep = gifts[0];
			var surplus = gifts.slice(1);
			var otherApplied = applied.filter(function (r) { return r !== rule; });
			var settled = keep && keep.quantity === 1 && !surplus.length &&
				freebieIsFree(terms, items, keep) && hasCode(cart, rule.code) && !otherApplied.length;
			if (settled) {
				setFreebieState('on');
				syncFreebieNudge();
				return none;
			}

			if (!keep) showFreebiePlaceholder();

			return unpoliced(function () {
				return inSequence(surplus, function (line) {
					return setLineQuantity(line.key, 0);
				}).then(function () {
					return keep && keep.quantity > 1 ? setLineQuantity(keep.key, 1) : null;
				}).then(function () {
					return keep ? null : addFreebie(terms);
				}).then(function () {
					return finishFreebie(terms, rule);
				});
			}).then(function (updated) {
				setFreebieState('on');
				corrections++;
				return { changed: true, revoked: false, cart: isCompleteCart(updated) ? updated : null, sections: updated && updated.sections ? updated.sections : null };
			}).catch(function (err) {
				console.error('[cart-offers] could not grant the free gift', err);
				corrections++;
				return { changed: true, revoked: false };
			});
		});
	}

	function claimFreebie() {
		var terms = freebieTerms();
		if (!terms) return Promise.reject(new Error('The free gift is not available.'));

		return getCart().then(function (cart) {
			var items = (cart && cart.items) || [];
			var rule = earnedFreebieRule(terms, items);
			if (!rule) throw new Error('Your cart no longer qualifies for the free gift.');

			var has = items.some(function (i) { return isFreebieLine(terms, i); });
			var add = has ? Promise.resolve() : addFreebie(terms);
			return add.then(function () { return finishFreebie(terms, rule); });
		});
	}

	/**
	 * Why the cart changed, in the shopper's words. Published on the config
	 * element so the copy stays in the theme editor rather than in this file.
	 * Null falls back to the gate's own default.
	 */
	function configMessage(attr) {
		var el = document.querySelector('[data-offers-config]');
		return (el && el.getAttribute(attr)) || null;
	}

	function giftRevokedMessage() {
		return configMessage('data-offers-revoked-message');
	}

	function comboRevokedMessage() {
		return configMessage('data-offer-c-revoked-message');
	}

	/**
	 * Run cart writes that enforcement itself must not react to. Without this,
	 * removing a surplus gift line is a cart write, which schedules another
	 * enforcement pass, which writes again — and claiming would be worse still,
	 * since claimGift adds the gift BEFORE applying the code and would have its
	 * gift deleted out from under it in that gap.
	 */
	function unpoliced(fn) {
		suppressed++;
		return Promise.resolve()
			.then(fn)
			.finally(function () { suppressed--; });
	}

	/**
	 * Drop offer A's code, preserving anything else the shopper had on.
	 * update.js replaces the entire set, so keeping the others means re-applying
	 * one rather than clearing and hoping.
	 */
	function releaseOfferCode(code) {
		if (!code) return Promise.resolve(null);

		return getCart().then(function (cart) {
			var codes = (cart && cart.discount_codes) || [];
			var mine = String(code).toUpperCase();
			var others = codes.filter(function (c) {
				return String(c.code).toUpperCase() !== mine;
			});

			if (others.length === codes.length) return null; // never applied
			return postJSON('/cart/update.js', { discount: others.length ? others[0].code : '' });
		});
	}

	function setLineQuantity(key, quantity) {
		return postJSON('/cart/change.js', { id: key, quantity: quantity });
	}

	/** Apply changes one at a time — line keys shift under concurrent writes. */
	function inSequence(list, step) {
		return list.reduce(function (chain, entry) {
			return chain.then(function () { return step(entry); });
		}, Promise.resolve());
	}

	/**
	 * Hold the cart to offer A's terms. Resolves true when it had to intervene,
	 * which is the caller's cue to re-render.
	 *
	 * Qualifying spend excludes the gift line, matching cart-offers.liquid —
	 * counting the gift would let a ₹1,499 shirt qualify a cart for its own
	 * ₹1,499 shirt.
	 */
	/**
	 * Is this response body a complete cart we can enforce against?
	 *
	 * add/change/update all hand back the post-write cart, which makes a separate
	 * GET /cart.js pure overhead. The shape is checked rather than assumed
	 * because the endpoints have historically differed on what they return, and
	 * acting on a partial cart would mean deleting a gift the shopper had legitimately
	 * earned. Anything unrecognised falls back to a real read.
	 */
	function isCompleteCart(data) {
		if (!data || !Array.isArray(data.items)) return false;
		if (typeof data.item_count !== 'number') return false;
		return data.items.every(function (i) {
			return i && typeof i.key === 'string' &&
				typeof i.handle === 'string' &&
				typeof i.final_line_price === 'number';
		});
	}

	/**
	 * Times enforcement has actually taken something back.
	 *
	 * The gate needs this to tell a shopper why their cart just changed even
	 * when the correction was made by the background pass rather than by its own
	 * check — otherwise a well-timed click lands in checkout with the gift
	 * silently gone, which is safe but baffling.
	 */
	var corrections = 0;

	/**
	 * Times enforcement has taken something AWAY, as opposed to merely writing
	 * to the cart.
	 *
	 * Split out from `corrections` once offer C arrived. `corrections` answers
	 * "is a cached cart body stale?", and an auto-applied combo code makes one
	 * stale just as a removed gift does. But only a LOSS may hold a checkout
	 * behind a warning — counting an application as one would stop a shopper to
	 * tell them their cart had got cheaper.
	 */
	var revocations = 0;

	/**
	 * Why the most recent revocation happened.
	 *
	 * A checkout can be held by a revocation made by an EARLIER background pass
	 * rather than by its own check, in which case the verifying pass has no
	 * message of its own to report. Without this it fell back to the gift copy
	 * and could tell a shopper their free gift was removed when what they had
	 * actually lost was the combo price.
	 */
	var lastRevokedMessage = null;

	/**
	 * One enforcement pass at a time.
	 *
	 * A checkout click now triggers a pass of its own, which can land on top of
	 * the background pass scheduled by the write the shopper just made. Both
	 * would read the same gift line and both would try to zero it; the loser
	 * posts a line key that no longer exists, gets a 422, and rejects — which
	 * the gate would read as "could not verify" and fail open, forwarding the
	 * shopper. Serialising removes the collision entirely.
	 */
	var enforceChain = Promise.resolve();

	function serialised(task) {
		var run = enforceChain.then(task, task);
		// Keep the chain alive whatever this pass did, or one rejection would
		// wedge every pass after it.
		enforceChain = run.then(function () {}, function () {});
		return run;
	}

	function enforceGiftLines(known) {
		var terms = offerTerms();
		if (!terms) return Promise.resolve(false);

		var load = known ? Promise.resolve(known) : getCart();
		return load.then(function (cart) {
			var items = (cart && cart.items) || [];
			var gifts = items.filter(function (i) { return i.handle === terms.handle; });
			if (!gifts.length) return false;

			var qualifying = items.reduce(function (sum, i) {
				return i.handle === terms.handle ? sum : sum + i.final_line_price;
			}, 0);

			// Spend no longer earns it: take back every copy and the code with
			// them, so re-adding the shirt later cannot come out free.
			if (qualifying < terms.threshold) {
				return unpoliced(function () {
					return inSequence(gifts, function (line) {
						return setLineQuantity(line.key, 0);
					}).then(function () {
						// Best-effort, and deliberately swallowed. Taking the line
						// away is what protects the order; tidying the now-toothless
						// code is housekeeping. Letting it reject would abandon the
						// re-render and leave a removed gift on screen — the very
						// thing being fixed.
						return releaseOfferCode(terms.code).catch(function (err) {
							console.error('[cart-offers] could not release ' + terms.code, err);
						});
					});
				}).then(function () {
					corrections++;
					revocations++;
					return true;
				});
			}

			// Still earned — but the reward is one shirt, however many were added
			// by hand from search or a collection page.
			var keep = gifts[0];
			var surplus = gifts.slice(1);
			if (!surplus.length && keep.quantity <= 1) return false;

			return unpoliced(function () {
				return inSequence(surplus, function (line) {
					return setLineQuantity(line.key, 0);
				}).then(function () {
					return keep.quantity > 1 ? setLineQuantity(keep.key, 1) : null;
				});
			}).then(function () {
				corrections++;
				revocations++;
				return true;
			});
		});
	}

	var enforceQueued = false;
	var pendingCart = null;

	/**
	 * Deferred to a macrotask so the caller's own re-render lands first.
	 * theme.js repaints the drawer synchronously inside its fetch handler; a
	 * refresh() raced in ahead of that would simply be overwritten with the
	 * stale markup we were trying to correct.
	 *
	 * `cart` is the body the triggering write already returned, used to skip a
	 * redundant read. When several writes coalesce into one pass it is dropped:
	 * the earliest cart is by then out of date, and a stale one could revoke a
	 * gift the later writes had just paid for.
	 */
	function scheduleEnforcement(cart) {
		if (enforceQueued) {
			pendingCart = null;
			return;
		}
		enforceQueued = true;
		pendingCart = cart || null;

		setTimeout(function () {
			enforceQueued = false;
			var known = pendingCart;
			pendingCart = null;
			var seen = shopperWrites;
			var correctionsAtSchedule = corrections;

			serialised(function () {
				// `known` is the body of the write that scheduled this pass. If a
				// pass we queued behind has since corrected the cart, that body
				// describes a cart that no longer exists — and acting on it would
				// mean re-posting line keys that are already gone. Fall back to a
				// real read in that case.
				var fresh = corrections !== correctionsAtSchedule ? null : known;
				return enforceAll(fresh);
			})
				.then(function (result) {
					var changed = result.changed;
					// This pass is usually enough on its own, so let it vouch for
					// the cart: the gate then waves the next Checkout click
					// straight through instead of paying for a second read. Only
					// when the shopper has not touched the cart again since, and
					// nothing is still on the wire — otherwise what we just
					// checked is already history.
					if (!changed && shopperWrites === seen && pendingWrites === 0) {
						var g = gate();
						if (g) g.markClean();
					}
					if (!changed) return null;
					if (result.sections && document.querySelector('.side-cart')) {
						if (shouldApplyRender(nextRenderToken())) renderSections(result.sections);
						return null;
					}
					// /cart renders no drawer (see layout/theme.liquid), so there
					// is nothing for refresh() to repaint and the page would keep
					// showing the line we just took away. Rare and self-limiting:
					// the reloaded cart is valid, so it cannot loop.
					if (!document.querySelector('.side-cart')) {
						window.location.reload();
						return null;
					}
					return refresh();
				})
				.catch(function (err) { console.error('[cart-offers] enforcement failed', err); });
		}, 0);
	}

	function claimGift(btn) {
		var variantId = btn.getAttribute('data-variant-id');
		var code = btn.getAttribute('data-offer-code');
		if (!variantId) return Promise.reject(new Error('Gift product has no variant'));

		// Gift must be in the cart before the code: Buy X Get Y is only
		// applicable once its "Y" line exists.
		return postJSON('/cart/add.js', { items: [{ id: Number(variantId), quantity: 1 }] })
			.then(function () { return applyDiscount(code); })
			.then(function (cart) { return assertApplied(cart, code); });
	}

	function claimDiscount(btn) {
		var code = btn.getAttribute('data-offer-code');
		var giftHandle = btn.getAttribute('data-gift-handle');
		// Drop the gift line, else the shopper keeps a now-full-price t-shirt.
		return removeGiftLines(giftHandle)
			.then(function () { return applyDiscount(code); })
			.then(function (cart) { return assertApplied(cart, code); });
	}

	document.addEventListener('click', function (e) {
		var claim = e.target.closest('[data-claim-offer]');
		if (claim) {
			e.preventDefault();
			if (busy) return;
			setBusy(claim, true);

			// Claims run unpoliced, so their writes never bump shopperWrites and
			// would leave an earlier confirmation standing over a cart that has
			// just gained a gift line. Void it by hand.
			markDirty();

			var claimRenderToken = nextRenderToken();
			var kind = claim.getAttribute('data-claim-offer');
			var run = unpoliced(function () {
				return kind === 'gift' ? claimGift(claim) : claimDiscount(claim);
			});

			// Always repaint, success or failure. A partial claim still changes
			// the cart — the gift gets added before the code is applied — so
			// bailing out early left the drawer showing a stale cart that was
			// missing a line the shopper had actually been charged nothing for.
			//
			// On success the applied-discount response already carries the
			// re-rendered drawer, so painting from it saves the shopper a whole
			// round-trip. Only the failure path has to go and fetch one.
			var failure = null;
			run
				.catch(function (err) { failure = err; return null; })
				.then(function (cart) {
					var sections = cart && cart.sections;
					if (!failure && sections) {
						if (shouldApplyRender(claimRenderToken)) renderSections(sections);
						return null;
					}
					return refresh();
				})
				.then(function () {
					if (!failure) return;
					// The button was replaced by the refresh; re-find it.
					var current = document.querySelector('[data-claim-offer="' + kind + '"]');
					if (current) showError(current, failure);
					else console.error('[cart-offers]', failure);
				})
				.finally(function () { setBusy(claim, false); });
			return;
		}

		var freebieClaim = e.target.closest('[data-freebie-claim]');
		if (freebieClaim) {
			e.preventDefault();
			if (busy) return;
			setBusy(freebieClaim, true);
			markDirty();

			var freebieRenderToken = nextRenderToken();
			var freebieFailure = null;
			unpoliced(claimFreebie)
				.catch(function (err) { freebieFailure = err; return null; })
				.then(function (updated) {
					var sections = updated && updated.sections ? updated.sections : null;
					if (!freebieFailure) setFreebieState('on');
					if (!freebieFailure && sections) {
						if (shouldApplyRender(freebieRenderToken)) renderSections(sections);
						return null;
					}
					return refresh();
				})
				.then(function () {
					if (!freebieFailure) return;
					var current = document.querySelector('[data-freebie-claim]');
					if (current) showError(current, freebieFailure);
					else console.error('[cart-offers]', freebieFailure);
				})
				.finally(function () { setBusy(freebieClaim, false); });
			return;
		}

		var comboAdd = e.target.closest('[data-combo-add]');
		if (comboAdd) {
			e.preventDefault();
			if (busy) return;

			var variantId = Number(comboAdd.getAttribute('data-variant-id'));
			if (!variantId) return;

			setBusy(comboAdd, true);

			var comboRenderToken = nextRenderToken();

			// Deliberately NOT unpoliced. This add completes the pair, and the
			// enforcement pass it triggers is exactly what applies the combo
			// code — suppressing it would leave the shopper holding both
			// products at full price.
			postJSON('/cart/add.js', withSections({ items: [{ id: variantId, quantity: 1 }] }))
				.then(function (cart) {
					// add.js with `sections` hands back the re-rendered drawer, so
					// paint from it rather than paying for another round-trip.
					// The banner still arrives a beat later, once enforcement has
					// applied the code and repainted again — the code cannot be
					// applied before the line it depends on exists.
					var sections = cart && cart.sections;
					if (sections) {
						if (shouldApplyRender(comboRenderToken)) renderSections(sections);
						return null;
					}
					return refresh();
				})
				.catch(function (err) {
					showError(comboAdd, err, 'Could not add this product.');
				})
				.finally(function () { setBusy(comboAdd, false); });
			return;
		}

		var size = e.target.closest('[data-gift-variant]');
		if (size && !size.disabled) {
			e.preventDefault();
			if (busy) return;
			var wrap = size.closest('[data-gift-sizes]');
			var lineKey = wrap && wrap.getAttribute('data-line-key');
			var newVariant = Number(size.getAttribute('data-gift-variant'));
			if (!lineKey || !newVariant) return;

			setBusy(size, true);
			markDirty(); // unpoliced too — same reason as the claim above.
			var sizeRenderToken = nextRenderToken();
			// change.js cannot swap a variant, so drop the line and re-add it.
			// Unpoliced because the cart is briefly gift-less between the two
			// calls, and enforcement would read that gap as a cancelled claim
			// and pull the discount code.
			unpoliced(function () {
				return postJSON('/cart/change.js', { id: lineKey, quantity: 0 })
					.then(function () {
						// The re-add is the last call, so let it bring the drawer
						// back with it rather than rendering separately.
						return postJSON('/cart/add.js', withSections({
							items: [{ id: newVariant, quantity: 1 }]
						}));
					});
			})
				.then(function (cart) {
					var sections = cart && cart.sections;
					if (sections) {
						if (shouldApplyRender(sizeRenderToken)) renderSections(sections);
						return null;
					}
					return refresh();
				})
				.catch(function (err) { console.error('[cart-offers]', err); })
				.finally(function () { setBusy(size, false); });
		}
	});

	/* ------------------------------------------------------------------ *
	 * Triggers
	 * ------------------------------------------------------------------ */

	/**
	 * Every route into the cart has to be covered, and theme.js reaches it from
	 * a dozen call sites (btyFetchCart for +/- and Remove, btyAddToCart, quick
	 * add, complementary products) with no event to subscribe to. Wrapping fetch
	 * catches all of them, plus any app or section added later, without
	 * threading a hook through theme.js by hand.
	 *
	 * Matches with and without the .js suffix, and tolerates a locale prefix
	 * (/en-in/cart/change), because routes.cart_change_url has neither shape
	 * guaranteed.
	 */
	var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

	(function policeCartWrites() {
		if (typeof window.fetch !== 'function') return;

		var original = window.fetch;

		function finishWrite() {
			noteWriteEnd();
			if (pendingWrites === 0) {
				var g = gate();
				if (g) g.setBusy(false);
			}
		}

		window.fetch = function (input, init) {
			var url = typeof input === 'string' ? input : (input && input.url) || '';
			var writesCart = CART_WRITE.test(String(url).split('#')[0]);
			if (!writesCart) return original.apply(this, arguments);

			var shopperDriven = suppressed === 0;

			// Void the gate's confirmation as the write is ISSUED, not when it
			// comes back. From this moment the cart is already in motion, and the
			// flight time is exactly the window shoppers were clicking Checkout
			// in. Waiting for the response to mark the cart unverified would
			// leave the hole open for the part that matters most.
			if (shopperDriven) {
				shopperWrites++;
				markDirty();
			}

			// Tracked for ALL cart writes, ours included: whenSettled() has to
			// know the wire is quiet before it trusts a /cart.js read.
			noteWriteStart();
			var busyGate = gate();
			if (busyGate) busyGate.setBusy(true);

			var result;
			try {
				result = original.apply(this, arguments);
			} catch (err) {
				finishWrite();
				throw err;
			}

			if (!result || typeof result.then !== 'function') {
				finishWrite();
				return result;
			}

			return result.then(function (response) {
				finishWrite();

				// A rejected write (sold out, bad line key) left the cart alone.
				if (!response || !response.ok) return response;

				// Our own corrective writes must not schedule another pass — that
				// is what unpoliced() is for.
				if (!shopperDriven) return response;

				// Read the cart out of a CLONE. The caller still needs the original
				// body unread — consuming it here would break every cart update in
				// theme.js. If the clone is unusable, enforcement reads /cart.js.
				try {
					response.clone().json().then(function (data) {
						scheduleEnforcement(isCompleteCart(data) ? data : null);
					}, function () {
						scheduleEnforcement(null);
					});
				} catch (e) {
					scheduleEnforcement(null);
				}

				return response;
			}, function (err) {
				finishWrite();
				throw err;
			});
		};
	})();

	/**
	 * Hand the gate its verifier. Done last so nothing can be asked of this
	 * module before its helpers exist.
	 */
	(function installGate() {
		var g = gate();
		if (g) g.install(verifyForCheckout);
	})();

	/**
	 * Carts persist across sessions, so a cart that went bad before this code
	 * shipped would stay bad forever without a pass on load. Skipped when the
	 * drawer is empty, which is the common case and costs a request otherwise.
	 */
	function enforceOnLoad() {
		// Matches cart lines in the drawer and on /cart alike, so the cheap
		// "empty cart, nothing to police" exit works on both.
		if (!document.querySelector('.product-item[data-line]')) return;
		scheduleEnforcement();
	}

	document.addEventListener('change', function (e) {
		var input = e.target;
		if (!input || typeof input.getAttribute !== 'function') return;
		var total = parseInt(input.getAttribute('data-group-total'), 10);
		if (!total) return;
		var line = parseInt(input.getAttribute('data-qty'), 10) || 0;
		var wanted = Number(String(input.value).trim());
		if (isNaN(wanted)) return;

		// A blank, negative or decimal quantity is never valid: put the shown total back and send
		// nothing, rather than treating it as a removal.
		if (String(input.value).trim() === '' || !Number.isInteger(wanted) || wanted < 0) {
			e.stopImmediatePropagation();
			input.value = total;
			return;
		}

		// Typing 0 means "remove this product". Zeroing only this line would
		// leave the group's other line(s) in the cart, which the drawer then
		// shows as the leftover quantity (1 or 2). Reuse the Remove button,
		// which already clears every line of the group.
		if (wanted === 0) {
			var row = input.closest('.product-item');
			var remove = row && row.querySelector('.product-remove');
			if (remove) {
				e.stopImmediatePropagation();
				remove.click();
				return;
			}
		}

		input.value = Math.max(0, line + (wanted - total));
		document.addEventListener('change', function () { input.value = wanted; }, { once: true });
	}, true);

	function lineSignature(item) {
		return item.variant_id + '|' + JSON.stringify(item.properties || {});
	}

	function removeGroupedLines(lines) {
		return getCart().then(function (cart) {
			var first = ((cart && cart.items) || [])[Math.min.apply(null, lines) - 1];
			if (!first) return null;
			var signature = lineSignature(first);

			function sweep(current, left) {
				var at = -1;
				((current && current.items) || []).some(function (item, n) {
					if (lineSignature(item) !== signature) return false;
					at = n;
					return true;
				});
				if (at === -1 || left === 0) return null;

				return postJSON('/cart/change.js', { line: at + 1, quantity: 0 }).then(function (updated) {
					return isCompleteCart(updated) ? sweep(updated, left - 1) : getCart().then(function (fresh) {
						return sweep(fresh, left - 1);
					});
				});
			}

			return sweep(cart, 10);
		});
	}

	document.addEventListener('click', function (e) {
		var remove = e.target && typeof e.target.closest === 'function' ? e.target.closest('.product-remove') : null;
		if (!remove) return;
		var row = remove.closest('.product-item');
		var marker = row && row.querySelector('[data-group-lines]');
		if (!marker) return;

		var lines = marker.getAttribute('data-group-lines').split(',').map(Number).filter(Boolean)
			.sort(function (a, b) { return b - a; });
		if (lines.length < 2) return;

		e.preventDefault();
		e.stopImmediatePropagation();
		row.classList.add('updating');
		markDirty();

		var token = nextRenderToken();
		unpoliced(function () {
			return removeGroupedLines(lines);
		}).then(function () {
			return getCart();
		}).then(function (cart) {
			return refresh().then(function () { scheduleEnforcement(cart); });
		}).catch(function (err) {
			console.error('[cart-offers] could not remove the grouped line', err);
			return refresh();
		});
	}, true);

	(function watchFreebieNudge() {
		if (typeof MutationObserver !== 'function') return;
		var queued = false;
		function start() {
			syncFreebieNudge();
			new MutationObserver(function () {
				if (queued) return;
				queued = true;
				window.requestAnimationFrame(function () {
					queued = false;
					syncFreebieNudge();
				});
			}).observe(document.body, { childList: true, subtree: true });
		}
		if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
		else start();
	})();

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', enforceOnLoad);
	} else {
		enforceOnLoad();
	}
})();
